import React from "react";
import { usePublicData } from "../lib/publicData";
import { Link } from "react-router-dom";
import { ChevronDown, Users, Clock, MapPin, Camera, Check, ArrowRight, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import Grain from "../components/Grain";

// 🚨 Booking gate — set to true once Stripe LIVE is configured & validated
const BOOKING_ENABLED = true;

const FAQ = [
  { q: "Que dois-je amener ?", a: "Ton boîtier, tes objectifs préférés (50mm, 85mm), 2 cartes mémoire vides, des batteries chargées. Le reste, on s'en occupe." },
  { q: "Quel niveau est requis ?", a: "Maîtrise du mode manuel obligatoire. Aucune expérience préalable avec le flash studio n'est nécessaire." },
  { q: "Le paiement est-il obligatoire à la réservation ?", a: "Oui, le paiement intégral est requis à l'inscription pour garantir ta place. C'est la seule façon de confirmer une réservation." },
  { q: "Que se passe-t-il si j'arrive en retard ?", a: "Une tolérance de 15 minutes est accordée pour absorber les imprévus (trafic, parking). Au-delà, la session continue comme prévu — le temps perdu n'est pas rattrapé en fin de session, par respect pour les autres participants." },
  { q: "Puis-je annuler ?", a: "Oui : annulation gratuite jusqu'à 14 jours avant la session (remboursement 100%). Entre J-14 et J-7, 50% du montant est remboursé. Moins de 7 jours avant : pas de remboursement, mais report possible sur une autre session dans les 3 mois (sous réserve de places). En cas de force majeure justifiée (maladie, deuil), report systématique." },
  { q: "Y aura-t-il un Niveau 02 ?", a: "Oui, prévu pour début 2027 : lumière éditoriale, mode/beauté, mix lumière naturelle et flash. Les alumni du Niveau 01 bénéficient d'un tarif préférentiel." },
];

const SETUPS = [
  {
    n: "01",
    titre: "One Light Mastery",
    sub: "Une source. Cinq patterns.",
    desc: "Vocabulaire fondamental du portrait studio : Loop, Rembrandt, Butterfly, Split, Broad/Short. Comprendre comment la position d'une seule source change tout.",
    img: "/images/generated/atelier_setup_01.jpg",
  },
  {
    n: "02",
    titre: "Multi-flash",
    sub: "Key + Fill + Rim",
    desc: "Construire la lumière comme un peintre construit une scène. Comprendre les ratios, le rôle de chaque source.",
    img: "/images/generated/atelier_setup_02.jpg",
  },
  {
    n: "03",
    titre: "Couleur & gélatines",
    sub: "Au-delà du portrait propre",
    desc: "Sortir du portrait propre. Créer une atmosphère cinématographique. Portraits bichromes, gélatines, grilles.",
    img: "/images/generated/atelier_setup_03.jpg",
  },
];

const REPARS = [
  "Le savoir-faire pour reproduire les 3 setups chez toi.",
  "1 × livret participant (à garder pour toujours).",
  "Des photos de toi prises pendant les workshops.",
  "Accès au groupe privé Alumni.",
  "Un tarif préférentiel pour le Niveau 02 (2027).",
];

function formatDateFR(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
}

function urgencyState(places_restantes, places_max) {
  if (places_restantes <= 0) return { tone: "complete", text: "COMPLET" };
  if (places_restantes === 1) return { tone: "max", text: "Dernière place ! 🔥" };
  if (places_restantes <= 2) return { tone: "high", text: `Plus que ${places_restantes} places` };
  if (places_restantes <= places_max / 2) return { tone: "mid", text: `${places_restantes} places restantes` };
  return { tone: "low", text: `${places_restantes} places disponibles` };
}

export default function Atelier() {
  const initial = usePublicData();
  const [sessions, setSessions] = React.useState(initial.sessions || []);
  React.useEffect(() => {
    api.get("/sessions", { params: { workshop_slug: "atelier-lumiere-n01" } }).then((r) => setSessions(r.data));
  }, []);

  return (
    <div className="section-dark">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden pt-28" data-testid="atelier-hero">
        <Grain />
        <div className="absolute inset-0 z-0">
          <img
            src="/images/generated/atelier_hero.jpg"
            alt="Atelier Lumière"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/70 to-dark/40" />
          <div className="absolute top-1/3 -right-40 w-[800px] h-[800px] glow-amber animate-glowPulse" style={{"--gx":"30%","--gy":"50%"}} />
        </div>
        <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-12 pb-24 w-full">
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:1 }}>
            <div className="eyebrow mb-6">Niveau 01 — Workshop d'éclairage</div>
            <h1 className="h-display text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[9rem]">
              L'Atelier<br/><span className="italic-accent">Lumière.</span>
            </h1>
            <p className="text-text-cream/80 font-light text-xl md:text-2xl mt-8 max-w-2xl font-serif italic">
              4 heures pour comprendre la lumière studio. 3 setups. 4 participants. Un modèle pro.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <a href="#sessions" className="btn-primary" data-testid="cta-voir-sessions">
                Voir les sessions <ArrowRight size={14} strokeWidth={1.5}/>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTE */}
      <section className="section-cream py-24 md:py-36 px-6 md:px-12" data-testid="atelier-manifeste">
        <div className="max-w-[1080px] mx-auto">
          <div className="eyebrow-dark mb-6">Manifeste</div>
          <p className="font-serif text-3xl md:text-5xl font-light italic leading-tight text-text-dark">
            « On ne devient pas bon en lumière en lisant. On devient bon en posant un flash,
            en regardant le résultat, en le déplaçant de 30 centimètres,<br/>
            <span className="text-accent-deep">et en regardant encore. »</span>
          </p>
          <div className="mt-12 space-y-5 text-text-dark/70 font-light text-lg leading-relaxed max-w-2xl">
            <p>
              L'Atelier Lumière, c'est 4 heures intenses pour comprendre vraiment ce qui se passe
              quand tu poses un softbox à droite plutôt qu'à gauche. Pas de blabla théorique,
              pas de slides : tu poses, tu shootes, tu regardes, tu ajustes.
            </p>
            <p>
              Tu repars avec les 3 setups de portrait studio les plus utiles, un livret participant,
              et l'habitude de penser la lumière plutôt que de la subir.
            </p>
          </div>
        </div>
      </section>

      {/* SETUPS */}
      <section className="section-dark py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" data-testid="atelier-setups">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] glow-amber-soft pointer-events-none" />
        <div className="max-w-[1480px] mx-auto relative z-10">
          <div className="eyebrow mb-4">Au programme</div>
          <h2 className="font-serif text-5xl md:text-6xl font-light mb-16">Trois setups<br/><span className="italic-accent">essentiels.</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {SETUPS.map((s) => (
              <div key={s.n} className="border-t border-accent-deep/40 pt-6" data-testid={`setup-${s.n}`}>
                <div className="aspect-[4/5] overflow-hidden mb-6 hover-zoom">
                  <img src={s.img} alt={s.titre} className="w-full h-full object-cover" />
                </div>
                <div className="font-serif italic text-accent-light text-2xl">Setup {s.n}</div>
                <h3 className="font-serif text-3xl mt-2">{s.titre}</h3>
                <div className="eyebrow mt-2 text-text-cream/50">{s.sub}</div>
                <p className="mt-4 text-text-cream/70 font-light leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMAT */}
      <section className="section-cream py-24 px-6 md:px-12" data-testid="atelier-format">
        <div className="max-w-[1280px] mx-auto">
          <div className="eyebrow-dark mb-4">Format & déroulé</div>
          <h2 className="font-serif text-5xl md:text-6xl font-light mb-16">Comment ça se <span className="italic text-accent-deep">passe.</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Clock, label: "Durée", val: "4 heures" },
              { icon: Users, label: "Participants", val: "Max 4" },
              { icon: Camera, label: "Matériel", val: "Godox fourni" },
              { icon: MapPin, label: "Lieu", val: "Studio Euclides, Écublens" },
            ].map((it) => (
              <div key={it.label} className="border-t border-text-dark/20 pt-5">
                <it.icon size={20} className="text-accent-deep" strokeWidth={1.5}/>
                <div className="eyebrow-dark mt-3 mb-1">{it.label}</div>
                <div className="font-serif text-xl">{it.val}</div>
              </div>
            ))}
          </div>
          <div className="mt-16 border border-accent-deep/40 p-8 md:p-10 bg-text-dark/5">
            <div className="eyebrow-dark mb-3 text-accent-deep">À noter — pré-requis</div>
            <p className="font-serif text-xl md:text-2xl italic leading-snug">
              Ce n'est pas un cours de photo, c'est un workshop de lumière.
            </p>
            <p className="mt-4 text-text-dark/70 font-light leading-relaxed">
              Pour profiter pleinement de cette journée, tu dois maîtriser ton boîtier en mode manuel
              (vitesse, ouverture, ISO, mise au point). On ne s'arrêtera pas pour expliquer les bases :
              on plonge directement dans la lumière.
            </p>
          </div>
        </div>
      </section>

      {/* CE QUE TU REPARS AVEC */}
      <section className="section-dark py-24 md:py-32 px-6 md:px-12" data-testid="atelier-repars">
        <div className="max-w-[1280px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">Tu repars avec</div>
            <h2 className="font-serif text-5xl md:text-6xl font-light leading-[0.95]">Ce que tu emportes<br/><span className="italic-accent">chez toi.</span></h2>
          </div>
          <div className="md:col-span-7 space-y-4">
            {REPARS.map((r) => (
              <div key={r} className="flex items-start gap-4 border-t border-white/10 pt-4">
                <Check size={18} className="text-accent-deep flex-shrink-0 mt-1" strokeWidth={1.5}/>
                <span className="font-light text-text-cream/80 text-lg">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SESSIONS */}
      <section id="sessions" className="section-cream py-24 md:py-32 px-6 md:px-12" data-testid="atelier-sessions">
        <div className="max-w-[1280px] mx-auto">
          <div className="eyebrow-dark mb-4">Sessions à venir</div>
          <h2 className="font-serif text-5xl md:text-6xl font-light mb-12">Les <span className="italic text-accent-deep">prochaines dates.</span></h2>

          {!BOOKING_ENABLED && (
            <div className="border border-accent-deep/40 bg-accent-deep/5 p-8 md:p-10 mb-12" data-testid="booking-notice">
              <div className="eyebrow-dark mb-3 text-accent-deep">Bientôt en ligne</div>
              <h3 className="font-serif text-3xl md:text-4xl italic leading-snug text-text-dark">
                Réservations bientôt <span className="text-accent-deep">disponibles.</span>
              </h3>
              <p className="mt-5 text-text-dark/75 font-light text-lg leading-relaxed max-w-2xl">
                Le système de réservation en ligne ouvre dans les jours à venir. Pour t'inscrire dès maintenant à une session, écris-moi en message direct sur Instagram ou via le formulaire de contact — je te confirme ta place en moins de 24h.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="https://www.instagram.com/studio.euclides" target="_blank" rel="noopener noreferrer" className="btn-dark" data-testid="cta-instagram-soon">
                  <Instagram size={14} strokeWidth={1.5}/> @studio.euclides
                </a>
                <Link to="/contact" className="btn-secondary" data-testid="cta-contact-soon" style={{borderColor:"rgba(26,26,26,0.4)", color:"var(--text-dark)"}}>
                  Me contacter <ArrowRight size={14} strokeWidth={1.5}/>
                </Link>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {sessions.length === 0 && <div className="text-text-dark/50 italic">Aucune session disponible pour le moment.</div>}
            {sessions.map((s) => {
              const full = s.places_restantes <= 0 || s.statut === "complete" || s.statut === "fermee" || s.statut === "annulee";
              const u = urgencyState(s.places_restantes, s.places_max);
              const toneClasses = {
                low: "text-text-dark/70",
                mid: "text-text-dark",
                high: "text-accent-deep font-medium",
                max: "text-accent-deep font-medium",
                complete: "text-text-dark/40 uppercase tracking-[0.3em] text-xs font-medium",
              }[u.tone];
              return (
                <div
                  key={s.id}
                  data-testid={`session-${s.id}`}
                  className={`grid md:grid-cols-12 gap-4 items-center border-t border-text-dark/20 py-6 ${full ? "opacity-60" : ""}`}
                >
                  <div className="md:col-span-3 relative">
                    <div className="font-serif text-3xl md:text-4xl italic text-accent-deep">{formatDateFR(s.date)}</div>
                    {full && (
                      <span className="inline-block mt-2 px-2 py-1 bg-text-dark/15 text-text-dark/50 text-[0.6rem] uppercase tracking-[0.3em]">
                        Complet
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-2 eyebrow-dark">{s.heure_debut} – {s.heure_fin}</div>
                  <div className="md:col-span-3 text-text-dark/70 font-light text-sm">{s.lieu}</div>
                  <div className="md:col-span-2">
                    <div className="font-serif text-2xl">CHF {s.prix_actuel}</div>
                    <div className={`text-xs mt-1 ${toneClasses}`}>{u.text}</div>
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    {!BOOKING_ENABLED ? (
                      <Link to="/contact" className="btn-dark" data-testid={`contact-instead-${s.id}`}>
                        Me contacter
                      </Link>
                    ) : full ? (
                      <button disabled className="btn-dark opacity-40 cursor-not-allowed" data-testid={`reserve-complet-${s.id}`}>
                        Complet
                      </button>
                    ) : (
                      <Link to={`/atelier/reservation/${s.id}`} className="btn-dark" data-testid={`reserve-session-${s.id}`}>
                        Réserver
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-dark py-24 md:py-32 px-6 md:px-12" data-testid="atelier-faq">
        <div className="max-w-[900px] mx-auto">
          <div className="eyebrow mb-4">FAQ</div>
          <h2 className="font-serif text-5xl md:text-6xl font-light mb-12">Les <span className="italic-accent">questions</span> fréquentes.</h2>
          <div className="space-y-2">
            {FAQ.map((f, i) => <FaqItem key={i} {...f} idx={i}/>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-dark py-32 px-6 md:px-12 text-center relative overflow-hidden" data-testid="atelier-cta-final">
        <div className="absolute inset-0 glow-amber" style={{"--gx":"50%","--gy":"50%"}}/>
        <Grain/>
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="font-serif text-5xl md:text-7xl font-light">Prêt à construire<br/><span className="italic-accent">ta lumière ?</span></h2>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            {BOOKING_ENABLED ? (
              <a href="#sessions" className="btn-primary">Voir les sessions disponibles <ArrowRight size={14} strokeWidth={1.5}/></a>
            ) : (
              <>
                <a href="https://www.instagram.com/studio.euclides" target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="cta-final-instagram">
                  <Instagram size={14} strokeWidth={1.5}/> @studio.euclides
                </a>
                <Link to="/contact" className="btn-secondary" data-testid="cta-final-contact">
                  Me contacter <ArrowRight size={14} strokeWidth={1.5}/>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border-t border-white/10" data-testid={`faq-item-${idx}`}>
      <button onClick={()=>setOpen(!open)} className="w-full py-6 flex items-center justify-between text-left group">
        <span className="font-serif text-xl md:text-2xl group-hover:text-accent-light transition">{q}</span>
        <ChevronDown size={22} className={`text-accent-deep transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5}/>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height:0, opacity:0 }}
            animate={{ height:"auto", opacity:1 }}
            exit={{ height:0, opacity:0 }}
            className="overflow-hidden"
          >
            <div className="pb-6 text-text-cream/70 font-light leading-relaxed">{a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
